import * as React from 'react';
import { eachDayOfInterval, isBefore } from 'date-fns';

// Material-UI
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Tab from '@mui/material/Tab';
import TabContext from '@mui/lab/TabContext';
import TabList from '@mui/lab/TabList';
import TabPanel from '@mui/lab/TabPanel';

// Styles
import { useTheme } from '@mui/material/styles';

// Components
import LodgeUseFilter from './LodgeUseFilter';
import Meals from './Meals';
import Nights from './Nights'
import Notes from './Notes';
import Passes from './Passes';

import {
  DateRange
} from '../../models';

// Date formatting hooks
import { useDates } from '../../hooks/UseDates';
// Hook for signup related dates
import { useSignupDates } from '../../hooks/UseSignupDates';

const LodgeUse: React.FC = () => {

  const { numberDays, numberNights } = useDates();
  const { getOffSeasonStartDate } = useSignupDates();

  const [activeTab, setActiveTab] = React.useState('1');
  const [clear, setClear] = React.useState<boolean>(false);
  const [dateRange, setDateRange] = React.useState<DateRange>(new DateRange());
  const [days, setDays] = React.useState<number>(0);
  const [disableActions, setDisableActions] = React.useState<boolean>(true);
  const [skiSeason, setSkiSeason] = React.useState<boolean>(false);
  const [nights, setNights] = React.useState<number>(0);

  // #region Styles

  const theme = useTheme();
  const styles = {
    root: {
      flexGrow: 1,
    },
    tabContainer: {
      height: 500,
      margin: theme.spacing(0, 0, 0, 0),
      padding: theme.spacing(1, 0, 1, 1),
    },
    tabHeading: {
      borderBottom: 1,
      borderColor: 'divider'
    },
    toolsContainer: {
      flexGrow: 1,
      padding: theme.spacing(1, 0, 1, 0),
    },
  }

  // #endregion

  // #region Event handlers

  /*
   * Handle clear of calendar components
   */
  const handleClear = () => {
    setDateRange(new DateRange());
    setClear(prevClear => !prevClear);
  }

  /*
   * Handle changes to filter
   */
  const handleFilterChanged = (range: DateRange) => {
    setDateRange(range);
  }

  /*
   * Handle refresh of calendar
   */
  const handleRefresh = () => {
  }

  /*
  * Handle tab change
  */
  const handleTabChange = (event: React.SyntheticEvent, tab: string) => {
    setActiveTab(tab);
  };

  React.useEffect(() => {
    if (dateRange.startDate && dateRange.endDate) {
      setDisableActions(false);
      setDays(numberDays(dateRange.startDate, dateRange.endDate));
      setNights(numberNights(dateRange.startDate, dateRange.endDate));

      const dr = eachDayOfInterval({
        start: new Date(dateRange.startDate),
        end: new Date(dateRange.endDate)
      });

      // Does date range include a ski season date?
      const skiSeasonDates = dr.filter((d: Date) => { return isBefore(d, new Date(getOffSeasonStartDate())) });
      setSkiSeason(skiSeasonDates.length > 0);

    } else {
      setActiveTab('1');
      setDisableActions(true);
      setDays(0);
      setNights(0);
      setSkiSeason(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange.startDate, dateRange.endDate]);

  // #endregion

  return (
    <>
      <Box sx={styles.root}>
        <Grid container sx={styles.toolsContainer}>
          <LodgeUseFilter
            clearFilter={clear}
            dateRange={dateRange}
            handleFilterChanged={handleFilterChanged}
            disableActions={disableActions}
            handleClear={handleClear}
            handleRefresh={handleRefresh}
          />
        </Grid>
        <TabContext value={activeTab}>
          <Box sx={styles.tabHeading}>
            <TabList onChange={handleTabChange}>
              <Tab label="Nights" value="1" />
              <Tab label="Notes" value="2" />
              {skiSeason &&
                <Tab label="Passes" value="3" />
              }
              <Tab label="Meals" value="4" />
            </TabList>
          </Box>
          <TabPanel value="1" sx={styles.tabContainer}>
            <Nights
              dateRange={dateRange}
              nights={nights}
            />
          </TabPanel>
          <TabPanel value="2" sx={styles.tabContainer}>
            <Notes
              dateRange={dateRange}
            />
          </TabPanel>
          {skiSeason &&
            <TabPanel value="3" sx={styles.tabContainer}>
              <Passes
                dateRange={dateRange}
                days={days}
              />
            </TabPanel>
          }
          <TabPanel value="4" sx={styles.tabContainer}>
            <Meals
              dateRange={dateRange}
              nights={nights}
            />
          </TabPanel>
        </TabContext>
      </Box>
    </>
  );

}

export default LodgeUse;